import React, {
  useCallback,
  useEffect,
  useState,
  useMemo,
  useRef,
} from 'react';
import DataEditor, {
  GridCellKind,
  GridColumn,
  Item,
  EditableGridCell,
  GridCell,
  GridSelection,
  CompactSelection,
} from '@glideapps/glide-data-grid';
import {
  ProSheetData,
  ProSheetCellValue,
  ProSheetCellMeta,
} from '@dreamai/types';
import '@glideapps/glide-data-grid/dist/index.css';
import './pro-doc-sheet-display.scss';
import { ReactComponent as CloseSourceInfoIcon } from '../../icons/close-source-info.svg';

interface ProDocSheetDisplayProps {
  proDocId: string;
  initialData?: ProSheetData;
  readOnly?: boolean;
  onDataChange?: (data: ProSheetData) => void;
}

const DEFAULT_COL_WIDTH = 150;
const DEFAULT_ROW_HEIGHT = 34;

/**
 * ProDocSheetDisplay - Spreadsheet component using Glide Data Grid
 * Supports formulas via HyperFormula
 */
export const ProDocSheetDisplay: React.FC<ProDocSheetDisplayProps> = ({
  proDocId,
  initialData,
  readOnly = false,
  onDataChange,
}) => {
  const [sheetData, setSheetData] = useState<ProSheetData>(
    () => initialData || { cells: {}, columns: [] },
  );

  // Row deletion confirmation state
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [pendingDeleteRows, setPendingDeleteRows] = useState<number[]>([]);

  // History stack for undo (max 50 states)
  const historyRef = useRef<ProSheetData[]>([]);
  const MAX_HISTORY = 50;

  // Save current state to history before making changes
  const saveToHistory = useCallback(() => {
    historyRef.current = [
      sheetData,
      ...historyRef.current.slice(0, MAX_HISTORY - 1),
    ];
  }, [sheetData]);

  // Undo - restore previous state
  const undo = useCallback(() => {
    if (historyRef.current.length === 0) return;

    const previousState = historyRef.current[0];
    historyRef.current = historyRef.current.slice(1);

    lastLocalChangeRef.current = Date.now();
    setSheetData(previousState);
    onDataChange?.(previousState);
  }, [onDataChange]);

  // Double-click detection for header rename
  const lastHeaderClickRef = useRef<{ colIndex: number; time: number } | null>(
    null,
  );
  const DOUBLE_CLICK_THRESHOLD = 300; // ms

  // Sorting state
  const [sortConfig, setSortConfig] = useState<{
    colIndex: number;
    direction: 'asc' | 'desc';
  } | null>(null);

  // Track row selection
  const [selection, setSelection] = useState<GridSelection>({
    columns: CompactSelection.empty(),
    rows: CompactSelection.empty(),
  });

  // Track if meta bar is dismissed for current cell
  const [metaBarDismissed, setMetaBarDismissed] = useState(false);

  // Reset meta bar dismissed state when cell selection changes
  const handleSelectionChange = useCallback(
    (newSelection: GridSelection) => {
      const oldCell = selection.current?.cell;
      const newCell = newSelection.current?.cell;
      if (oldCell?.[0] !== newCell?.[0] || oldCell?.[1] !== newCell?.[1]) {
        setMetaBarDismissed(false);
      }
      setSelection(newSelection);
    },
    [selection],
  );

  // Container ref and dimensions for DataEditor sizing
  const containerRef = useRef<HTMLDivElement>(null);
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });

  // Use ResizeObserver to track container size
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const observer = new ResizeObserver((entries) => {
      const { width, height } = entries[0].contentRect;
      setDimensions({ width, height });
    });

    observer.observe(container);
    return () => observer.disconnect();
  }, []);

  // Calculate actual number of rows and columns from data
  const numRows = useMemo(() => {
    const cellKeys = Object.keys(sheetData.cells);
    if (cellKeys.length === 0) return 100; // Default if no data

    const maxRow = cellKeys.reduce((max, key) => {
      const row = parseInt(key.split(',')[0], 10);
      return Math.max(max, row);
    }, 0);

    // Add buffer rows for editing (at least 100 total)
    return Math.max(maxRow + 50, 100);
  }, [sheetData.cells]);

  const numCols = useMemo(() => {
    const cellKeys = Object.keys(sheetData.cells);
    if (cellKeys.length === 0) return 26; // Default if no data

    const maxCol = cellKeys.reduce((max, key) => {
      const col = parseInt(key.split(',')[1], 10);
      return Math.max(max, col);
    }, 0);

    // Add buffer columns for editing (at least 26 total)
    return Math.max(maxCol + 10, 26);
  }, [sheetData.cells]);

  // Track when we last made a local change
  const lastLocalChangeRef = React.useRef<number>(0);
  const prevProDocIdRef = React.useRef(proDocId);

  // Sync initialData when proDocId changes OR when data changes from external source
  useEffect(() => {
    if (!initialData) return;

    // If proDocId changed, always update (new sheet)
    if (proDocId !== prevProDocIdRef.current) {
      setSheetData(initialData);
      prevProDocIdRef.current = proDocId;
      return;
    }

    // If data changed but we just made a local change, ignore it (prevent loop)
    const timeSinceLocalChange = Date.now() - lastLocalChangeRef.current;
    if (timeSinceLocalChange < 500) {
      return; // Ignore updates within 1 second of local change
    }

    // Otherwise, it's an external update - merge only changed cells
    setSheetData((prevData) => {
      // Check if cells, columns, or cellMeta actually changed
      const cellsChanged =
        JSON.stringify(prevData.cells) !== JSON.stringify(initialData.cells);
      const columnsChanged =
        JSON.stringify(prevData.columns) !==
        JSON.stringify(initialData.columns);
      const cellMetaChanged =
        JSON.stringify(prevData.cellMeta) !==
        JSON.stringify(initialData.cellMeta);

      if (!cellsChanged && !columnsChanged && !cellMetaChanged) {
        return prevData; // No changes, keep current state
      }

      // Merge: keep structure but update only changed cells and metadata
      return {
        ...prevData,
        cells: initialData.cells,
        columns: initialData.columns || prevData.columns,
        cellMeta: initialData.cellMeta || prevData.cellMeta,
      };
    });
  }, [proDocId, initialData]);

  // Generate column definitions
  const columns: GridColumn[] = useMemo(() => {
    return Array.from({ length: numCols }, (_, i) => {
      const colLetter = String.fromCharCode(65 + (i % 26));
      const colDef = sheetData.columns?.find((c) => c.index === i);
      return {
        title: colDef?.header || colLetter,
        id: `col-${i}`,
        width: colDef?.width || DEFAULT_COL_WIDTH,
      };
    });
  }, [numCols, sheetData.columns]);

  // Get cell content - support text, number, boolean, and bubble types
  const getCellContent = useCallback(
    ([col, row]: Item): GridCell => {
      const key = `${row},${col}`;
      const cellValue = sheetData.cells[key];

      // Handle empty cells
      if (cellValue === undefined || cellValue === null) {
        return {
          kind: GridCellKind.Text,
          data: '',
          displayData: '',
          allowOverlay: true,
        };
      }

      // Handle boolean cells
      if (typeof cellValue === 'boolean') {
        return {
          kind: GridCellKind.Boolean,
          data: cellValue,
          allowOverlay: false, // Boolean cells are edited directly, no overlay
        };
      }

      // Handle bubble cells
      if (
        typeof cellValue === 'object' &&
        cellValue !== null &&
        !Array.isArray(cellValue) &&
        'kind' in cellValue &&
        cellValue.kind === 'bubble'
      ) {
        return {
          kind: GridCellKind.Bubble,
          data: cellValue.text,
          allowOverlay: false, // Bubble cells don't support overlay editing
        };
      }

      // Handle text and number cells (display both as text for now)
      return {
        kind: GridCellKind.Text,
        data: String(cellValue),
        displayData: String(cellValue),
        allowOverlay: true,
      };
    },
    [sheetData.cells],
  );

  // Handle cell edits
  const onCellEdited = useCallback(
    (cell: Item, newValue: EditableGridCell) => {
      if (readOnly) return;
      saveToHistory();

      const [col, row] = cell;
      const key = `${row},${col}`;

      let cellValue: ProSheetCellValue;

      // Convert based on cell kind
      if (newValue.kind === GridCellKind.Boolean) {
        cellValue = (newValue as any).data;
      } else if (newValue.kind === GridCellKind.Text) {
        // Try to parse as number if it looks like one
        const str = newValue.data;
        const num = Number(str);
        if (!isNaN(num) && str.trim() !== '') {
          cellValue = num;
        } else {
          cellValue = str;
        }
      } else {
        // For other types, just store as string
        cellValue = String((newValue as any).data);
      }

      const updatedCells = {
        ...sheetData.cells,
        [key]: cellValue,
      };

      const updatedData: ProSheetData = {
        ...sheetData,
        cells: updatedCells,
      };

      lastLocalChangeRef.current = Date.now();
      setSheetData(updatedData);
      onDataChange?.(updatedData);
    },
    [sheetData, readOnly, onDataChange, saveToHistory],
  );

  // Handle column resize
  const onColumnResize = useCallback(
    (column: GridColumn, newSize: number) => {
      if (!column.id) return;
      saveToHistory();
      const colIndex = parseInt(column.id.replace('col-', ''), 10);
      const updatedColumns = [...(sheetData.columns || [])];
      const existingCol = updatedColumns.find((c) => c.index === colIndex);

      if (existingCol) {
        existingCol.width = newSize;
      } else {
        updatedColumns.push({ index: colIndex, width: newSize });
      }

      const updatedData: ProSheetData = {
        ...sheetData,
        columns: updatedColumns,
      };

      lastLocalChangeRef.current = Date.now();
      setSheetData(updatedData);
      onDataChange?.(updatedData);
    },
    [sheetData, onDataChange, saveToHistory],
  );

  // Handle column header click for renaming
  const onHeaderClicked = useCallback(
    (colIndex: number, _event: unknown) => {
      if (readOnly) return;

      const colDef = sheetData.columns?.find((c) => c.index === colIndex);
      const currentHeader =
        colDef?.header || String.fromCharCode(65 + (colIndex % 26));

      const newHeader = window.prompt('Enter column name:', currentHeader);
      if (newHeader === null || newHeader === currentHeader) return;

      saveToHistory();

      const updatedColumns = [...(sheetData.columns || [])];
      const existingCol = updatedColumns.find((c) => c.index === colIndex);

      if (existingCol) {
        existingCol.header = newHeader || undefined;
      } else {
        updatedColumns.push({
          index: colIndex,
          header: newHeader || undefined,
        });
      }

      const updatedData: ProSheetData = {
        ...sheetData,
        columns: updatedColumns,
      };

      lastLocalChangeRef.current = Date.now();
      setSheetData(updatedData);
      onDataChange?.(updatedData);
    },
    [readOnly, sheetData, onDataChange, saveToHistory],
  );

  // Prompt for row deletion
  const promptDeleteRows = useCallback(() => {
    if (readOnly) return;
    const selectedRows = selection.rows.toArray();
    if (selectedRows.length === 0) return;
    setPendingDeleteRows(selectedRows);
    setShowDeleteConfirm(true);
  }, [readOnly, selection.rows]);

  // Clear row contents only (no shift)
  const clearRowContents = useCallback(
    (rowsToDelete: number[]) => {
      if (readOnly) return;
      saveToHistory();

      const newCells: Record<string, ProSheetCellValue> = {
        ...sheetData.cells,
      };

      // Remove cells from the selected rows
      Object.keys(newCells).forEach((key) => {
        const [cellRow] = key.split(',').map(Number);
        if (rowsToDelete.includes(cellRow)) {
          delete newCells[key];
        }
      });

      const updatedData: ProSheetData = {
        ...sheetData,
        cells: newCells,
      };

      lastLocalChangeRef.current = Date.now();
      setSheetData(updatedData);
      onDataChange?.(updatedData);

      setSelection({
        columns: CompactSelection.empty(),
        rows: CompactSelection.empty(),
      });
    },
    [readOnly, sheetData, onDataChange, saveToHistory],
  );

  // Delete rows and shift cells up
  const deleteRowsAndShift = useCallback(
    (rowsToDelete: number[]) => {
      if (readOnly) return;
      saveToHistory();

      const newCells: Record<string, ProSheetCellValue> = {};
      const cellKeys = Object.keys(sheetData.cells);

      // Get max row to know how many rows exist
      const maxRow = cellKeys.reduce((max, key) => {
        const row = parseInt(key.split(',')[0], 10);
        return Math.max(max, row);
      }, 0);

      // Shift rows up to fill gaps
      let rowOffset = 0;
      for (let row = 0; row <= maxRow; row++) {
        if (rowsToDelete.includes(row)) {
          rowOffset++;
          continue;
        }

        // Copy cells from this row to the new position
        cellKeys.forEach((key) => {
          const [cellRow, cellCol] = key.split(',').map(Number);
          if (cellRow === row) {
            const newKey = `${row - rowOffset},${cellCol}`;
            newCells[newKey] = sheetData.cells[key];
          }
        });
      }

      const updatedData: ProSheetData = {
        ...sheetData,
        cells: newCells,
      };

      lastLocalChangeRef.current = Date.now();
      setSheetData(updatedData);
      onDataChange?.(updatedData);

      setSelection({
        columns: CompactSelection.empty(),
        rows: CompactSelection.empty(),
      });
    },
    [readOnly, sheetData, onDataChange, saveToHistory],
  );

  // Handle confirmation choice
  const handleDeleteConfirm = useCallback(
    (shiftUp: boolean) => {
      if (shiftUp) {
        deleteRowsAndShift(pendingDeleteRows);
      } else {
        clearRowContents(pendingDeleteRows);
      }
      setShowDeleteConfirm(false);
      setPendingDeleteRows([]);
    },
    [pendingDeleteRows, deleteRowsAndShift, clearRowContents],
  );

  // Handle keyboard shortcuts (Cmd+Z for undo)
  const onKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key === 'z') {
        event.preventDefault();
        undo();
      }
    },
    [undo],
  );

  // Handle delete callback from DataEditor
  const onDelete = useCallback(
    (selection: GridSelection): boolean => {
      if (readOnly) return false;

      // If rows are selected, show confirmation prompt
      if (selection.rows.length > 0) {
        promptDeleteRows();
        return false; // We handled it
      }

      // Return true to let DataEditor handle cell deletion
      return true;
    },
    [readOnly, promptDeleteRows],
  );

  return (
    <>
      <div
        ref={containerRef}
        style={{
          width: '100%',
          flex: '1 1 0',
          minHeight: 0,
          border: '1px solid #e0e0e0',
          borderRadius: '8px',
          overflow: 'hidden',
        }}
        tabIndex={0}
        onKeyDown={onKeyDown}
      >
        {dimensions.width > 0 && dimensions.height > 0 && (
          <DataEditor
            width={dimensions.width}
            height={dimensions.height}
            getCellContent={getCellContent}
            columns={columns}
            rows={numRows}
            onCellEdited={onCellEdited}
            onColumnResize={onColumnResize}
            onHeaderClicked={onHeaderClicked}
            onDelete={onDelete}
            rowHeight={DEFAULT_ROW_HEIGHT}
            gridSelection={selection}
            onGridSelectionChange={handleSelectionChange}
            rowSelect='multi'
            rowSelectionMode='auto'
            smoothScrollX={true}
            smoothScrollY={true}
            getCellsForSelection={true}
            rowMarkers='clickable-number'
            rowMarkerStartIndex={0}
            theme={{
              accentColor: '#4F46E5',
              accentLight: '#E0E7FF',
              textDark: '#1F2937',
              textMedium: '#6B7280',
              textLight: '#9CA3AF',
              textBubble: '#1F2937',
              bgIconHeader: '#F9FAFB',
              fgIconHeader: '#6B7280',
              textHeader: '#374151',
              textGroupHeader: '#111827',
              bgCell: '#FFFFFF',
              bgCellMedium: '#F9FAFB',
              bgHeader: '#F3F4F6',
              bgHeaderHasFocus: '#E5E7EB',
              bgHeaderHovered: '#E5E7EB',
              borderColor: '#E5E7EB',
              horizontalBorderColor: '#F3F4F6',
            }}
          />
        )}
      </div>

      {/* Cell metadata status bar */}
      {(() => {
        if (metaBarDismissed) return null;
        const currentCell = selection.current?.cell;
        if (!currentCell) return null;
        const [col, row] = currentCell;
        const cellKey = `${row},${col}`;
        const meta = sheetData.cellMeta?.[cellKey];
        if (!meta || meta.type !== 'source') return null;

        // Check if sourceUrl is a proper website URL (not coreSignal or internal source)
        const isWebsiteSource =
          meta.sourceUrl &&
          (meta.sourceUrl.startsWith('http://') ||
            meta.sourceUrl.startsWith('https://')) &&
          !meta.sourceUrl.includes('coresignal');

        return (
          <div className='cell-meta-bar'>
            <button
              className='close-button'
              onClick={() => setMetaBarDismissed(true)}
              aria-label='Close source info'
            >
              <CloseSourceInfoIcon />
            </button>
            <span className='source-label'>
              <strong>Source:</strong>{' '}
              {isWebsiteSource ? (
                <a
                  href={meta.sourceUrl}
                  target='_blank'
                  rel='noopener noreferrer'
                  className='source-url'
                >
                  {new URL(meta.sourceUrl!).hostname}
                </a>
              ) : (
                'Data'
              )}
            </span>
            {meta.sourceExplanation && (
              <span className='source-explanation'>
                {meta.sourceExplanation}
              </span>
            )}
          </div>
        );
      })()}

      {/* Row deletion confirmation modal */}
      {showDeleteConfirm && (
        <div
          className='delete-modal-overlay'
          onClick={() => setShowDeleteConfirm(false)}
        >
          <div className='delete-modal' onClick={(e) => e.stopPropagation()}>
            <h3 className='delete-modal-title'>
              Delete {pendingDeleteRows.length} row
              {pendingDeleteRows.length > 1 ? 's' : ''}?
            </h3>
            <p className='delete-modal-message'>
              Would you like to shift the cells below up, or just clear the
              contents?
            </p>
            <div className='delete-modal-actions'>
              <button
                className='btn-cancel'
                onClick={() => setShowDeleteConfirm(false)}
              >
                Cancel
              </button>
              <button
                className='btn-clear'
                onClick={() => handleDeleteConfirm(false)}
              >
                Clear contents
              </button>
              <button
                className='btn-shift'
                onClick={() => handleDeleteConfirm(true)}
              >
                Shift cells up
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ProDocSheetDisplay;
